import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fetchEbooks } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { HERO_ART } from "@/lib/heroes";
import CardImage from "@/components/CardImage";
import Seo from "@/components/Seo";
import { PIC } from "@/lib/images";
import { stripHtml } from "@/lib/html";
import { BookOpen, FileText } from "lucide-react";

const EbookCard = ({ b, pick }) => (
  <Link to={`/books/${b.slug}`} data-testid={`ebook-card-${b.slug}`} className="group flex h-full flex-col overflow-hidden rounded-xl border border-[var(--gold)]/25 bg-surface shadow-[0_6px_24px_rgba(44,38,33,0.05)] transition-all duration-300 hover:-translate-y-1 hover:border-[var(--gold)]/50 hover:shadow-[0_16px_44px_rgba(199,80,0,0.13)]">
    <CardImage src={b.cover_url || PIC.manuscript} alt={b.title} ratio="aspect-[3/4]" fallback={<FileText className="h-10 w-10 text-gold/50" />} />
    <div className="flex flex-1 flex-col p-5">
      {b.category && <span className="overline-label text-saffron">{b.category}</span>}
      <h3 className="mt-1 font-serif-latin text-lg leading-snug text-ink">{pick(b.title_hi, b.title) || b.title}</h3>
      {(b.author || b.year) && <p className="mt-1 text-xs text-ink-3">{[b.author, b.year].filter(Boolean).join(" · ")}</p>}
      <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-2 line-clamp-3">{stripHtml(pick(b.description_hi, b.description) || b.description)}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-saffron transition-transform group-hover:translate-x-1">
        <BookOpen className="h-4 w-4" /> {pick("पढ़ें", "Read")}
      </span>
    </div>
  </Link>
);

const Shelf = ({ label, books, pick, onSeeAll }) => (
  <section className="mb-14" data-testid={`ebook-shelf-${label}`}>
    <div className="mb-5 flex items-end justify-between gap-4 border-b border-line pb-2">
      <h2 className="font-serif-latin text-2xl text-ink">{label} <span className="text-base text-ink-3">· {books.length}</span></h2>
      {books.length > 5 && onSeeAll && (
        <button onClick={onSeeAll} className="shrink-0 text-sm text-saffron hover:underline" data-testid={`ebook-shelf-seeall-${label}`}>{pick("सभी देखें", "See all")} →</button>
      )}
    </div>
    <div className="-mx-1 flex snap-x gap-6 overflow-x-auto px-1 pb-4 [scrollbar-width:thin]">
      {books.map((b) => (
        <div key={b.slug} className="w-56 shrink-0 snap-start sm:w-60">
          <EbookCard b={b} pick={pick} />
        </div>
      ))}
    </div>
  </section>
);

export default function Ebooks() {
  const { pick } = useLang();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEbooks().then((d) => { setItems(d.items || []); setCats(d.categories || []); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const OTHER = pick("अन्य", "Other");
  const shelves = useMemo(() => {
    const map = {};
    for (const b of items) {
      const key = b.category || OTHER;
      (map[key] = map[key] || []).push(b);
    }
    const ordered = [...cats.filter((c) => map[c]), ...(map[OTHER] ? [OTHER] : [])];
    return ordered.map((c) => ({ label: c, books: map[c] }));
  }, [items, cats, OTHER]);

  const shown = cat === "all" ? items : items.filter((b) => (b.category || OTHER) === cat);

  return (
    <div data-testid="ebooks-page">
      <Seo path="/books" title="eBooks & Manuscripts" description="A curated digital library of sacred eBooks, scriptures and manuscripts of Bharat — read online, and sign in to save and download." keywords="Hindu ebooks, sacred manuscripts, scriptures pdf, Vedas pdf, Puranas download, spiritual books" />
      <PageHero
        art={HERO_ART.knowledge}
        overline={pick("ग्रंथालय", "Digital Library")}
        deva="ग्रंथ एवं पांडुलिपियाँ"
        en={pick("पुस्तकें एवं पांडुलिपियाँ", "eBooks & Manuscripts")}
        tagline={pick("शास्त्र, ग्रंथ एवं दुर्लभ पांडुलिपियों का संग्रह — ऑनलाइन पढ़ें; सहेजने एवं डाउनलोड हेतु साइन इन करें।", "A curated collection of scriptures, eBooks and rare manuscripts — read online; sign in to save and download.")}
      />
      <section className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-16">
        {cats.length > 0 && (
          <div className="mb-10 flex flex-wrap gap-2" data-testid="ebook-category-filter">
            <button onClick={() => setCat("all")} data-testid="ebook-cat-all" className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${cat === "all" ? "border-saffron bg-[var(--saffron)] text-white" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`}>{pick("सभी", "All")}</button>
            {cats.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`ebook-cat-${c}`} className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${cat === c ? "border-saffron bg-[var(--saffron)] text-white" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`}>{c}</button>
            ))}
          </div>
        )}
        {loading ? (
          <p className="py-20 text-center text-ink-3">…</p>
        ) : items.length === 0 ? (
          <p className="py-20 text-center text-ink-3">{pick("अभी कोई ग्रंथ उपलब्ध नहीं है।", "No eBooks available yet — please check back soon.")}</p>
        ) : cat === "all" && shelves.length > 1 ? (
          <div data-testid="ebooks-shelves">
            {shelves.map((s) => (
              <Shelf key={s.label} label={s.label} books={s.books} pick={pick} onSeeAll={() => setCat(s.label)} />
            ))}
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="ebooks-grid">
            {shown.map((b) => <EbookCard key={b.slug} b={b} pick={pick} />)}
          </div>
        )}
      </section>
    </div>
  );
}
